import React from "react";
import { useTranslation } from "react-i18next";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Work } from "../../types/works";
import { WorkPriorityBadge, WorkStatusBadge, WorkTypeBadge } from "./badges";
import {
  formatDateTime,
  getDriverName,
  getVehicleLabel,
  routeLabel,
} from "./utils";

interface WorksTableProps {
  works: Work[];
  loading: boolean;
  onView: (work: Work) => void;
  onEdit: (work: Work) => void;
  onDelete: (work: Work) => void;
}

const formatMetricPair = (planned?: number, actual?: number, unit = "") => {
  const hasPlanned = planned !== undefined && planned !== null;
  const hasActual = actual !== undefined && actual !== null;
  if (!hasPlanned && !hasActual) return "—";
  const fmt = (v: number) => (Number.isInteger(v) ? v.toString() : v.toFixed(1));
  if (hasPlanned && hasActual) return `${fmt(actual!)}/${fmt(planned!)} ${unit}`.trim();
  if (hasActual) return `${fmt(actual!)} ${unit}`.trim();
  return `${fmt(planned!)} ${unit}`.trim();
};

export const WorksTable: React.FC<WorksTableProps> = ({ works, loading, onView, onEdit, onDelete }) => {
  const { t } = useTranslation("works");

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-[#F5F4F0] border-b border-[#ECEAE4]" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-[#FAFAF8] border-b border-[#ECEAE4]" />
        ))}
      </div>
    );
  }

  if (works.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-medium text-[#2B2F2E]">{t("table.empty.title")}</p>
        <p className="text-sm text-[#8A9590] mt-1">{t("table.empty.subtitle")}</p>
      </div>
    );
  }

  const route = (work: Work) => routeLabel(work.primaryObject, work.secondaryObject);

  return (
    <>
      {/* ── Mobile card list (hidden on sm+) ── */}
      <div className="sm:hidden divide-y divide-[#ECEAE4]">
        {works.map((work) => (
          <div key={work.id} className="px-3 py-2.5 hover:bg-[#FAFAF8] transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => onView(work)}
                  className="text-sm font-semibold text-[#2B2F2E] hover:text-[#C97A3C] transition-colors leading-snug"
                >
                  {work.workNumber}
                </button>
                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                  <WorkTypeBadge type={work.type} />
                  <WorkPriorityBadge priority={work.priority} />
                  <WorkStatusBadge status={work.status} />
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  type="button"
                  onClick={() => onView(work)}
                  title={t("actions.view")}
                  className="p-1.5 rounded-md text-[#8A9590] hover:text-[#2B2F2E] hover:bg-[#F0EFEB] transition-colors"
                >
                  <Eye size={15} />
                </button>
                {work.status !== "completed" && (
                  <button
                    type="button"
                    onClick={() => onEdit(work)}
                    title={t("actions.edit")}
                    className="p-1.5 rounded-md text-[#8A9590] hover:text-[#C97A3C] hover:bg-[#FCEEE2] transition-colors"
                  >
                    <Pencil size={15} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(work)}
                  title={t("actions.delete")}
                  className="p-1.5 rounded-md text-[#8A9590] hover:text-[#E24B4A] hover:bg-[#FBEAEA] transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            {route(work) !== "—" && (
              <div className="text-xs text-[#5A6460] truncate mt-1">{route(work)}</div>
            )}
            <div className="flex items-center justify-between gap-2 mt-1 text-xs text-[#8A9590]">
              <span className="truncate">{getDriverName(work)} · {getVehicleLabel(work)}</span>
              <span className="shrink-0 tabular-nums">{formatDateTime(work.plannedStart)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Desktop table (hidden on mobile) ── */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-[#ECEAE4]">
          <thead className="bg-[#FAFAF8]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.work")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.route")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.driver")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.metrics")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.planned")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.status")}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-[#8A9590] uppercase tracking-wider whitespace-nowrap">
                {t("table.columns.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#ECEAE4]">
            {works.map((work) => (
              <tr key={work.id} className="hover:bg-[#FAFAF8] transition-colors duration-150">
                <td className="px-4 py-3 align-top">
                  <button
                    type="button"
                    onClick={() => onView(work)}
                    className="text-sm font-semibold text-[#2B2F2E] hover:text-[#C97A3C] transition-colors"
                  >
                    {work.workNumber}
                  </button>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <WorkTypeBadge type={work.type} />
                    <WorkPriorityBadge priority={work.priority} />
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="text-sm text-[#2B2F2E] max-w-[220px] truncate" title={route(work)}>
                    {route(work)}
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="text-sm text-[#2B2F2E]">{getDriverName(work)}</div>
                  <div className="text-xs text-[#8A9590] mt-0.5">{getVehicleLabel(work)}</div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex flex-col gap-0.5 text-xs text-[#2B2F2E]">
                    <span>
                      <span className="text-[#8A9590]">{t("metrics.tonnageShort")}:</span>{" "}
                      {formatMetricPair(work.tonnage.planned, work.tonnage.actual, "t")}
                    </span>
                    <span>
                      <span className="text-[#8A9590]">{t("metrics.distanceShort")}:</span>{" "}
                      {formatMetricPair(work.distanceKm.planned, work.distanceKm.actual, "km")}
                    </span>
                    <span>
                      <span className="text-[#8A9590]">{t("metrics.hoursShort")}:</span>{" "}
                      {formatMetricPair(work.hours.planned, work.hours.actual, "h")}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 align-top whitespace-nowrap">
                  <div className="text-sm text-[#2B2F2E]">{formatDateTime(work.plannedStart)}</div>
                  {work.plannedEnd && (
                    <div className="text-xs text-[#8A9590] mt-0.5">→ {formatDateTime(work.plannedEnd)}</div>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <WorkStatusBadge status={work.status} />
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => onView(work)}
                      title={t("actions.view")}
                      className="p-1.5 rounded-md text-[#8A9590] hover:text-[#2B2F2E] hover:bg-[#F0EFEB] transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                    {work.status !== "completed" && (
                      <button
                        type="button"
                        onClick={() => onEdit(work)}
                        title={t("actions.edit")}
                        className="p-1.5 rounded-md text-[#8A9590] hover:text-[#C97A3C] hover:bg-[#FCEEE2] transition-colors"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(work)}
                      title={t("actions.delete")}
                      className="p-1.5 rounded-md text-[#8A9590] hover:text-[#E24B4A] hover:bg-[#FBEAEA] transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
